import { KeyboardEvent, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { useEQM2050_R01Store } from "./Store_EQM2050_R01";
import useUiuxStore from "src/store/useUiuxStore";
import { base64ToBlob, downloadExcel } from "src/utils/blob/blobUtil";
import { openPdfBlob, parseBlobError } from "src/utils/pdf/pdfUtil";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import qz from 'qz-tray';
import {
	getEQM2050_R01List,
	getEQM2050_R01AllCount,
	postEQM2050_R01,
	patchEQM2050_R01,
	deleteEQM2050_R01,
	downloadExcel_EQM2050_R01,
	downloadPdf_EQM2050_R01,
	EQM2050_R01PdfParams,
} from "./Api_EQM2050_R01";
import { defaultEQM2050_R01Dto, EQM2050_R01Dto } from "./Dto_EQM2050_R01";
import { buildDefaultInspectionDt, buildMonthlyInspectionRows, buildPdfParamsFromRow, resolvePdfSelectedRow } from "./Utils_EQM2050_R01";
import { InsertRowHandler } from "src/utils/grid/GridHandlerContent";
import { useQzPrint } from "src/hooks/useQzPrint";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM2050_R01Dto> | null>;
	setClickable: (clickable: boolean) => void;
}

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_EQM2050_R01 = (props: Props) => {
	const { gridRef, setClickable } = props;

	useFetchActionbarAuth("EQM2050_R01");
	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { printPdfFromBlob } = useQzPrint();

	const {
		EQM2050_R01List,
		setEQM2050_R01List,
		insertEQM2050_R01List,
		selectedEQM2050_R01List,
		setSelectedEQM2050_R01List,
		removeSelectedEQM2050_R01List,
		editEQM2050_R01,
		setEditEQM2050_R01,
		EQM2050_R01Filter,
		setEQM2050_R01Filter,
		EQM2050_R01PastFilter,
		setEQM2050_R01PastFilter,
		originalEQM2050_R01List,
		setOriginalEQM2050_R01List,
		deletedEQM2050_R01List,
		addDeletedEQM2050_R01List,
		removeDeletedEQM2050_R01List,
		removeEQM2050_R01List,
		initData,
		setScrollTop,
	} = useEQM2050_R01Store();

	const { setMessage } = useUiuxStore();

	const { compCdList } = useCommonCodeStore();

	const fetchData = useCallback(async (arg?: FetchOptions | KeyboardEvent<HTMLInputElement>) => {
		const messageMode = arg && typeof arg === 'object' && 'messageMode' in arg
			? arg.messageMode ?? 'show'
			: 'show';
		setLoadingSpinner(true);
		if (messageMode === 'show') {
			setScrollTop(null);
		}

		const defaultCompCd = compCdList[0]?.compCd ?? '';
		const searchFilter = {
			...EQM2050_R01Filter,
			compCd: EQM2050_R01Filter.compCd?.trim() ? EQM2050_R01Filter.compCd : defaultCompCd,
		};

		const response = await getEQM2050_R01List('search', searchFilter);
		const responseData = response.data.data as EQM2050_R01Dto[];

		const { displayList, originalList } = buildMonthlyInspectionRows(searchFilter, responseData);
		setEQM2050_R01List(displayList);

		// 원본 데이터 저장 (수정 여부 판단용)
		setOriginalEQM2050_R01List(originalList);

		if (responseData && responseData.length > 0) {
			setEQM2050_R01PastFilter(searchFilter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${responseData.length}건)`);
			}
		} else {
			if (messageMode === 'show') {
				showWarning('조회된 데이터가 없습니다.');
			}
		}
		setLoadingSpinner(false);

		return response.data.data;
	}, [EQM2050_R01Filter, compCdList, setEQM2050_R01List, insertEQM2050_R01List, setEQM2050_R01PastFilter, setOriginalEQM2050_R01List, setLoadingSpinner, setMessage, setScrollTop]);

	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);
		try {
			

			const defaultCompCd = compCdList[0]?.compCd ?? '';
			const searchFilter = {
				...EQM2050_R01Filter,
				compCd: EQM2050_R01Filter.compCd?.trim() ? EQM2050_R01Filter.compCd : defaultCompCd,
			};
			
			const response = await downloadExcel_EQM2050_R01('', searchFilter);
			const blob = base64ToBlob(
				response.data.data,
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			);
			downloadExcel(blob, '생활용수.전력량 등록.xlsx');
			showSuccess('엑셀 다운로드가 완료되었습니다.');
		} catch (error) {
			console.error('엑셀 다운로드 실패:', error);
			showWarning('엑셀 다운로드에 실패했습니다.');
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM2050_R01Filter, setLoadingSpinner]);

	const resolvePdfParams = useCallback((): EQM2050_R01PdfParams | null => {
		const selectedRow = resolvePdfSelectedRow(
			selectedEQM2050_R01List,
			EQM2050_R01List,
			deletedEQM2050_R01List
		);

		if (!selectedRow) {
			showWarning('PDF 출력할 행을 1개 선택하세요.');
			return null;
		}

		if (!selectedRow.inspectionDt) {
			showWarning('선택한 행의 일자를 입력하세요.');
			return null;
		}

		const defaultCompCd = compCdList[0]?.compCd ?? '';
		return buildPdfParamsFromRow(
			EQM2050_R01Filter,
			selectedRow,
			EQM2050_R01Filter.compCd?.trim() ? EQM2050_R01Filter.compCd : defaultCompCd
		);
	}, [
		selectedEQM2050_R01List,
		EQM2050_R01List,
		deletedEQM2050_R01List,
		EQM2050_R01Filter,
		compCdList,
	]);

	const handlePrintClick = useCallback(async (formType: 'form18' | 'form7') => {
		if (!EQM2050_R01Filter.inspectionMonth) {
			showWarning('조회 월을 선택하세요.');
			return;
		}

		const searchFilter = resolvePdfParams();
		if (!searchFilter) {
			return;
		}

		const formLabel = formType === 'form18' ? '별지 제18호서식' : '별지 제7호서식';

		setLoadingSpinner(true);
		try {
			const response = await downloadPdf_EQM2050_R01(formType, searchFilter);
			const blob = new Blob([response.data], { type: 'application/pdf' });

			if (qz.websocket.isActive()) {
				const ok = await printPdfFromBlob(blob, 'report1');
				if (ok) {
					showSuccess(`${formLabel} PDF가 출력되었습니다.`);
				} else {
					openPdfBlob(blob);
				}
			} else {
				openPdfBlob(blob);
				showSuccess(`${formLabel} PDF가 출력되었습니다.`);
			}
		} catch (error) {
			console.error(`${formLabel} PDF 출력 실패:`, error);
			showWarning(await parseBlobError(error));
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM2050_R01Filter, printPdfFromBlob, setLoadingSpinner, resolvePdfParams]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleDeleteClick = useCallback(() => {
		if (deletedEQM2050_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedEQM2050_R01List(deletedEQM2050_R01List);
			removeSelectedEQM2050_R01List();
			// 원본 데이터로 복구
			setEQM2050_R01List([...originalEQM2050_R01List]);
		} else if (selectedEQM2050_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedEQM2050_R01List(selectedEQM2050_R01List);
			removeSelectedEQM2050_R01List(selectedEQM2050_R01List);
		}
	}, [selectedEQM2050_R01List, deletedEQM2050_R01List, addDeletedEQM2050_R01List, removeDeletedEQM2050_R01List, removeSelectedEQM2050_R01List, setEQM2050_R01List, originalEQM2050_R01List]);

	// 상단 '등록' 버튼 전용 핸들러
	const handleCreateClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		const defaultAnalysisDt = buildDefaultInspectionDt(EQM2050_R01Filter);
		const defaultRowDataWithDate = {
			...defaultEQM2050_R01Dto,
			inspectionDt: defaultAnalysisDt,
		};

		InsertRowHandler({
			event: e,
			dataList: EQM2050_R01List,
			rowData: { ...defaultRowDataWithDate, valueIndex: -1 },
			insertRow: insertEQM2050_R01List,
			defaultRowData: defaultRowDataWithDate,
			setClickable
		});
	}, [EQM2050_R01List, EQM2050_R01Filter, insertEQM2050_R01List, setClickable]);

	// 유효성 검증 함수: 중복 일자 및 빈 일자 체크
	const validateAnalysisDt = useCallback((): boolean => {
		// 삭제되지 않은 활성 행만 필터링
		const activeList = EQM2050_R01List.filter(row => 
			!deletedEQM2050_R01List.includes(row.valueIndex) && 
			row.inspectionDt
		);

		// 1. 중복 일자 체크
		const dateCountMap = new Map<string, number>();
		activeList.forEach(row => {
			const count = dateCountMap.get(row.inspectionDt) || 0;
			dateCountMap.set(row.inspectionDt, count + 1);
		});

		const duplicatedDates = Array.from(dateCountMap.entries())
			.filter(([_, count]) => count > 1)
			.map(([date, _]) => date);

		if (duplicatedDates.length > 0) {
			showWarning(`중복된 분석일자가 있습니다: ${duplicatedDates.join(', ')}`);
			return false;
		}

		// 2. 빈 일자 체크
		const emptyDateRows = EQM2050_R01List.filter(row => 
			!deletedEQM2050_R01List.includes(row.valueIndex) && 
			!row.inspectionDt
		);

		if (emptyDateRows.length > 0) {
			showWarning('분석일자가 입력되지 않은 행이 있습니다.');
			return false;
		}

		return true;
	}, [EQM2050_R01List, deletedEQM2050_R01List]);

	const handleSaveClick = useCallback(async () => {
		// 유효성 검증
		if (!validateAnalysisDt()) {
			return;
		}

		// 신규 데이터 처리 (valueIndex 기준으로 삭제 확인 - 숫자 비교)
		const insertedList: EQM2050_R01Dto[] = EQM2050_R01List.filter(row =>
			!deletedEQM2050_R01List.includes(row.valueIndex) &&
			(
				row.inserted === true ||
				(row.isModified && originalEQM2050_R01List.find(original => original.valueIndex === row.valueIndex)?.inserted === true)
			)
		);

		// 수정된 데이터 처리 (valueIndex 기준으로 삭제 확인 - 숫자 비교)
		const modifiedList: EQM2050_R01Dto[] = EQM2050_R01List.filter(row =>
			!deletedEQM2050_R01List.includes(row.valueIndex) &&
			row.isModified &&
			!row.inserted &&
			originalEQM2050_R01List.find(original => original.valueIndex === row.valueIndex)?.inserted !== true
		);

		const defaultCompCd = compCdList[0]?.compCd ?? '';
		const applyDefaultCompCd = (list: EQM2050_R01Dto[]) => list.map(row => ({
			...row,
			compCd: row.compCd?.trim() ? row.compCd : defaultCompCd,
		}));
		const saveInsertedList = applyDefaultCompCd(insertedList);
		const saveModifiedList = applyDefaultCompCd(modifiedList);

		// 삭제 대상 리스트 (valueIndex 기준으로 확인 - 숫자 비교, API는 analysisDt로 호출)
		const deleteTargetList = EQM2050_R01List.filter(item =>
			deletedEQM2050_R01List.includes(item.valueIndex)
		);

		try {
			setLoadingSpinner(true);

			const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
				saveInsertedList.length > 0 ? postEQM2050_R01(saveInsertedList) : Promise.resolve(),
				saveModifiedList.length > 0 ? patchEQM2050_R01(saveModifiedList) : Promise.resolve(),
				deleteTargetList.length > 0 ? Promise.all(deleteTargetList.map(item => deleteEQM2050_R01(item.inspectionDt))) : Promise.resolve()
			]);

			if (insertedResponse?.data.successFlag) {
				showSuccess('용수.전력사용량 정보 저장이 완료되었습니다.');
			} else if (modifiedResponse?.data.successFlag) {
				showSuccess('용수.전력사용량 정보 수정이 완료되었습니다.');
			} else if (deleteResponse?.every(item => item.data.successFlag === true) ?? false) {
				showSuccess('용수.전력사용량 정보 삭제가 완료되었습니다.');
			}

			// 저장 후 조회
			await fetchData({ messageMode: 'none' });
			
			// 삭제 목록 초기화
			removeDeletedEQM2050_R01List();
			removeSelectedEQM2050_R01List();
		} catch (error: any) {
			console.error('저장 실패:', error);
			const errorMessage = error?.response?.data?.message || error?.message || '저장에 실패했습니다.';
			showWarning(errorMessage);
		} finally {
			setLoadingSpinner(false);
		}
	}, [EQM2050_R01List, deletedEQM2050_R01List, originalEQM2050_R01List, compCdList, removeDeletedEQM2050_R01List, removeSelectedEQM2050_R01List, fetchData, setLoadingSpinner, validateAnalysisDt]);

	return {
		fetchData,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleExcelClick,
		handleSaveClick,
		handlePdfClick: handlePrintClick,
	};
};

